import { STRBooking } from './types';

export const channelColors: Record<string, string> = {
  '자리톡': '#e0f2fe',
  '리브애니웨어': '#dbeafe',
  '직방': '#fff7ed',
  '삼삼엠투': '#f3e8ff',
  '엔코스테이': '#fef9c3',
  '에어비앤비': '#fce7f3',
  '기타': '#dcfce7',
};

export const channelBorderColors: Record<string, string> = {
  '자리톡': '#0284c7',
  '리브애니웨어': '#1d4ed8',
  '직방': '#ea580c',
  '삼삼엠투': '#7c3aed',
  '엔코스테이': '#a16207',
  '에어비앤비': '#db2777',
  '기타': '#16a34a',
};

export function getChannelBg(ch: string): string {
  return channelColors[ch] || '#e3f2fd';
}

export function formatNumber(n: number): string {
  return n.toLocaleString('ko-KR');
}

export function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseNumberInput(value: string): number {
  return parseInt(value.replace(/,/g, ''), 10) || 0;
}

export function calcOverlapDays(b: STRBooking, mStart: string, mEnd: string): number {
  const ci = b.check_in > mStart ? b.check_in : mStart;
  const co = b.check_out < mEnd ? b.check_out : mEnd;
  if (ci > co) return 0;
  const d1 = new Date(ci + 'T00:00:00');
  const d2 = new Date(co + 'T00:00:00');
  return Math.round((d2.getTime() - d1.getTime()) / 86400000) + 1;
}

export interface ProrataResult {
  gross: number;
  rent: number;
  maint: number;
  clean: number;
  negAdj: number;
  platformFee: number;
}

export function calcProrata(b: STRBooking, overlapDays: number): ProrataResult {
  const totalDays = b.days || 1;
  const ratio = overlapDays / totalDays;
  const rentP = Math.round((b.total_rent || 0) * ratio);
  const maintP = Math.round((b.total_maintenance || 0) * ratio);
  const cleanP = Math.round((b.total_cleaning || 0) * ratio);
  const gross = rentP + maintP + cleanP;
  let negAdj = 0;
  if (b.negotiated_price) {
    const finalP = Math.round((b.negotiated_price || 0) * ratio);
    negAdj = finalP - gross;
  }
  return {
    gross,
    rent: rentP,
    maint: maintP,
    clean: cleanP,
    negAdj,
    platformFee: Math.round((b.platform_fee || 0) * ratio),
  };
}

export interface MonthSettlement {
  gross: number;
  rent: number;
  maint: number;
  clean: number;
  negAdj: number;
  platformFee: number;
  cleaningExpense: number;
  count: number;
  occupiedDays: number;
  availDays: number;
  daysInMonth: number;
  occupancy: number;
}

export function calcMonthSettlement(
  bookings: STRBooking[],
  year: number,
  month: number,
  opStart: string | null
): MonthSettlement {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const mStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const mEnd = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

  let availStart = mStart;
  if (opStart && opStart > mStart && opStart <= mEnd) availStart = opStart;
  let availDays: number;
  if (opStart && mEnd < opStart) {
    availDays = 0;
  } else {
    const s = new Date(availStart + 'T00:00:00');
    const e = new Date(mEnd + 'T00:00:00');
    availDays = Math.round((e.getTime() - s.getTime()) / 86400000) + 1;
  }

  let gross = 0, rent = 0, maint = 0, clean = 0, negAdj = 0;
  let platformFee = 0, cleaningExpense = 0, count = 0;
  const occupiedSet: Record<string, boolean> = {};

  bookings.forEach((b) => {
    const overlap = calcOverlapDays(b, mStart, mEnd);
    if (overlap <= 0) return;
    count++;
    const pr = calcProrata(b, overlap);
    gross += pr.gross;
    rent += pr.rent;
    maint += pr.maint;
    clean += pr.clean;
    negAdj += pr.negAdj;
    platformFee += pr.platformFee;
    if (b.check_in >= mStart && b.check_in <= mEnd) cleaningExpense += (b.cleaning_expense || 0);
    const ci = b.check_in > mStart ? b.check_in : mStart;
    const co = b.check_out < mEnd ? b.check_out : mEnd;
    const d = new Date(ci + 'T00:00:00');
    const end = new Date(co + 'T00:00:00');
    while (d <= end) {
      occupiedSet[formatDate(d)] = true;
      d.setDate(d.getDate() + 1);
    }
  });

  const occupiedDays = Object.keys(occupiedSet).length;
  const occupancy = availDays > 0 ? Math.round(occupiedDays / availDays * 100) : 0;

  return { gross, rent, maint, clean, negAdj, platformFee, cleaningExpense, count, occupiedDays, availDays, daysInMonth, occupancy };
}

export function calcSubleaseRent(
  subleaseRentFull: number,
  year: number,
  month: number,
  opStart: string | null,
  endDate?: string | null
): number {
  if (!subleaseRentFull) return 0;
  const dim = new Date(year, month + 1, 0).getDate();
  const mStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const mEnd = `${year}-${String(month + 1).padStart(2, '0')}-${String(dim).padStart(2, '0')}`;
  if (opStart && mEnd < opStart) return 0;
  let startDay = 1;
  if (opStart && opStart > mStart && opStart <= mEnd) {
    startDay = parseInt(opStart.substring(8, 10));
  }
  let endDay = dim;
  if (endDate && endDate >= mStart && endDate < mEnd) {
    endDay = parseInt(endDate.substring(8, 10));
  }
  const useDays = endDay - startDay + 1;
  if (useDays <= 0) return 0;
  return Math.round(subleaseRentFull / dim * useDays);
}
